import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from '@/components/icons';
import { imageSrcSet, sizedImage } from '@/utils/images';
import { useDragScroll } from '@/utils/useDragScroll';
import type { ImageFormats } from '@/types/chat';

export interface Slide {
  url: string;
  formats?: ImageFormats;
  width?: number;
}

/**
 * A product's photos blown up over the page, as the product page does it: one photo as large
 * as the screen allows, arrows (and the keyboard's) to go through them, the thumbnails beside
 * it (under it on a narrow screen), "n / N". ✕, Escape or a click beside the photo closes it,
 * handing back the photo it was on, and the focus to where it was. Swipe on a touch screen,
 * drag with a mouse; the page under it doesn't scroll.
 */
export function Lightbox({
  slides,
  start,
  title,
  onClose,
}: {
  slides: Slide[];
  start: number;
  title: string;
  onClose: (index: number) => void;
}): JSX.Element {
  const [index, setIndex] = useState(start);
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dragScroll = useDragScroll((track) => track.clientWidth);
  const now = useRef({ index, onClose });
  now.current = { index, onClose };
  const many = slides.length > 1;

  function goTo(i: number, smooth = true) {
    const n = Math.max(0, Math.min(slides.length - 1, i));
    setIndex(n);
    const track = trackRef.current;
    track?.scrollTo?.({ left: n * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
  }
  const close = () => now.current.onClose(now.current.index);

  // opens on the photo it was asked for, the focus on ✕
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (track) track.scrollLeft = start * track.clientWidth;
    closeRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape closes, the arrow keys go through the photos; a wheel moves the thumbnails only
  useEffect(() => {
    const root = rootRef.current;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'ArrowRight') goTo(now.current.index + 1);
      else if (e.key === 'ArrowLeft') goTo(now.current.index - 1);
    };
    const onWheel = (e: WheelEvent) => {
      if (!(e.target instanceof Element && e.target.closest('.cal-lb-thumbs'))) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    root?.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('keydown', onKey);
      root?.removeEventListener('wheel', onWheel);
      opener?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // the current thumbnail in view (a column beside the photo, a row under it)
  useEffect(() => {
    const row = thumbsRef.current;
    const thumb = row?.children[index] as HTMLElement | undefined;
    if (!row || !thumb) return;
    row.scrollTo?.({
      top: thumb.offsetTop - (row.clientHeight - thumb.clientHeight) / 2,
      left: thumb.offsetLeft - (row.clientWidth - thumb.clientWidth) / 2,
      behavior: 'smooth',
    });
  }, [index]);

  const beside = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) close();
  };

  return createPortal(
    <div className="calebasse-chatbot-root cal-fragment">
      <div ref={rootRef} className="cal-lb" role="dialog" aria-modal="true" aria-label={`Photos — ${title}`} onClick={beside}>
        <button ref={closeRef} type="button" aria-label="Fermer" onClick={close} className="cal-lb-close">
          <XIcon width={22} height={22} />
        </button>
        <div className="cal-lb-body" onClick={beside}>
          {many && (
            <div ref={thumbsRef} className="cal-lb-thumbs">
              {slides.map((s, i) => (
                <button
                  key={s.url}
                  type="button"
                  aria-label={`Photo ${i + 1}`}
                  aria-current={i === index}
                  onClick={() => goTo(i)}
                  className="cal-lb-thumb gtmtrack"
                  content-content="chatbot-fiche-photo"
                >
                  <img src={sizedImage(s.url, s.formats, 'thumbnail')} alt="" draggable={false} />
                </button>
              ))}
            </div>
          )}
          <div className="cal-lb-stage">
            <div
              ref={trackRef}
              className={`cal-lb-track${many ? ' is-scrollable' : ''}`}
              {...dragScroll}
              onScroll={(e) => {
                const t = e.currentTarget;
                if (t.clientWidth) setIndex(Math.round(t.scrollLeft / t.clientWidth));
              }}
            >
              {slides.map((s, i) => (
                <div key={s.url} className="cal-lb-slide">
                  <img
                    src={sizedImage(s.url, s.formats, 'large')}
                    srcSet={imageSrcSet(s.url, s.formats, s.width)}
                    sizes="(min-width: 900px) min(100vh, 1000px), 100vw"
                    alt={i === index ? title : ''}
                    loading={Math.abs(i - start) <= 1 ? 'eager' : 'lazy'}
                    draggable={false}
                  />
                </div>
              ))}
            </div>
            {many && (
              <>
                <button type="button" aria-label="Photo précédente" disabled={index === 0} onClick={() => goTo(index - 1)} className="cal-lb-arrow is-prev gtmtrack" content-content="chatbot-fiche-photo">
                  <ChevronLeftIcon width={22} height={22} />
                </button>
                <button type="button" aria-label="Photo suivante" disabled={index === slides.length - 1} onClick={() => goTo(index + 1)} className="cal-lb-arrow is-next gtmtrack" content-content="chatbot-fiche-photo">
                  <ChevronRightIcon width={22} height={22} />
                </button>
                <span className="cal-lb-counter">
                  {index + 1} / {slides.length}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
